/**
 * MKS-339 — API client for the helpdesk inbox, its configuration surfaces and
 * the one new triage mutation.
 *
 * Shapes mirror `backend/src/api/schemas/helpdesk.ts` verbatim. A field that
 * exists here but not there arrives `undefined` at runtime — `fast-json-stringify`
 * drops undeclared response properties silently — so the two files are edited
 * together.
 */
import { apiGet, apiPost, apiPatch, apiPut, apiDelete } from './client';

// ---------------------------------------------------------------------------
// The inbox
// ---------------------------------------------------------------------------

export const HELPDESK_STATUSES = ['triage', 'open', 'closed'] as const;
export type HelpdeskStatus = (typeof HELPDESK_STATUSES)[number];

export const HELPDESK_SLA_STATES = ['none', 'ok', 'warning', 'breached'] as const;
export type HelpdeskSlaState = (typeof HELPDESK_SLA_STATES)[number];

export type HelpdeskView = 'needs_attention' | 'all';

export interface HelpdeskSlaMilestone {
  elapsedMs: number;
  targetMinutes: number | null;
  percentage: number | null;
  stopped: boolean;
}

export interface HelpdeskRequest {
  taskId: number;
  code: string;
  name: string;
  projectId: number;
  projectName: string;
  customerId: number | null;
  customerName: string | null;
  requesterName: string | null;
  requesterKind: 'external_user' | 'user' | null;
  categoryId: number | null;
  categoryName: string | null;
  priority: string;
  tierId: number | null;
  tierName: string | null;
  tierOrderIndex: number | null;
  status: HelpdeskStatus;
  stateName: string | null;
  ownerId: number | null;
  ownerName: string | null;
  slaState: HelpdeskSlaState;
  response: HelpdeskSlaMilestone;
  resolution: HelpdeskSlaMilestone;
  createdAt: string;
  lastActivityAt: string;
}

/**
 * The filter set, in exactly the shape the URL carries it. `owner` is a
 * string because it holds two different things: the literal 'unassigned', or
 * a user id — see the backend route's note.
 */
export interface HelpdeskFilters {
  view?: HelpdeskView;
  status?: HelpdeskStatus;
  slaState?: HelpdeskSlaState;
  projectId?: string;
  customerId?: string;
  categoryId?: string;
  owner?: string;
}

export interface HelpdeskSummary {
  open: number;
  inTriage: number;
  breaching: number;
  hasAccess: boolean;
}

export interface HelpdeskFilterOptions {
  projects: Array<{ id: number; name: string }>;
  customers: Array<{ id: number; name: string }>;
  categories: Array<{ id: number; projectId: number; name: string }>;
  owners: Array<{ id: number; name: string }>;
}

/**
 * Drop empty values so the URL never carries `?status=` — the backend's
 * querystring schema enumerates its enums and an empty string is not one of
 * them, which would 400 the whole request for a filter the user just cleared.
 */
export function helpdeskFiltersToQuery(filters: HelpdeskFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

export function fetchHelpdeskRequests(filters: HelpdeskFilters) {
  return apiGet<{ data: HelpdeskRequest[] }>(
    `/helpdesk/requests${helpdeskFiltersToQuery(filters)}`,
  );
}

export function fetchHelpdeskSummary() {
  return apiGet<{ data: HelpdeskSummary }>('/helpdesk/summary');
}

export function fetchHelpdeskFilterOptions() {
  return apiGet<{ data: HelpdeskFilterOptions }>('/helpdesk/filter-options');
}

// ---------------------------------------------------------------------------
// Request categories
// ---------------------------------------------------------------------------

export interface RequestCategory {
  id: number;
  projectId: number;
  name: string;
  orderIndex: number;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

export function fetchRequestCategories(projectId: number, includeArchived = false) {
  return apiGet<{ data: RequestCategory[] }>(
    `/projects/${projectId}/request-categories${includeArchived ? '?includeArchived=true' : ''}`,
  );
}

/**
 * The portal's copy of the list, for the client's request form.
 *
 * Goes through the same transport as `createExternalTask` in `board.ts`: the
 * backend route is `authenticateAny` and accepts the external session, and
 * `client.ts`'s 401 branch already recognises an `/external/` pathname and
 * leaves the external session alone. The endpoint is external-gated
 * server-side — the handler asserts the actor is a member of the project.
 */
export function fetchExternalRequestCategories(projectId: number | string) {
  return apiGet<{ data: RequestCategory[] }>(
    `/projects/${projectId}/external/request-categories`,
  );
}

export function createRequestCategory(
  projectId: number,
  input: { name: string; orderIndex?: number },
) {
  return apiPost<{ data: RequestCategory }>(
    `/projects/${projectId}/request-categories`,
    input,
  );
}

/**
 * The whole ordering in one call. `categoryIds` must be every category of the
 * project, archived ones included, in the wanted order — the server numbers
 * them 0..n-1 and answers 422 for anything else. One request rather than N
 * PATCHes, so a move cannot half-apply.
 */
export function reorderRequestCategories(projectId: number, categoryIds: number[]) {
  return apiPut<{ data: RequestCategory[] }>(
    `/projects/${projectId}/request-categories/order`,
    { categoryIds },
  );
}

export function updateRequestCategory(
  projectId: number,
  categoryId: number,
  input: { name?: string; orderIndex?: number; archived?: boolean },
) {
  return apiPatch<{ data: RequestCategory }>(
    `/projects/${projectId}/request-categories/${categoryId}`,
    input,
  );
}

export function deleteRequestCategory(projectId: number, categoryId: number) {
  return apiDelete<{ data: { success: boolean } }>(
    `/projects/${projectId}/request-categories/${categoryId}`,
  );
}

// ---------------------------------------------------------------------------
// Canned replies
// ---------------------------------------------------------------------------

export interface CannedReply {
  id: number;
  /** null = global (offered on every project). */
  projectId: number | null;
  title: string;
  body: string;
  createdById: number | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * With a projectId: that project's replies plus every global one,
 * project-scoped first. Without: the global ones only.
 */
export function fetchCannedReplies(projectId?: number | null) {
  return apiGet<{ data: CannedReply[] }>(
    projectId != null ? `/canned-replies?projectId=${projectId}` : '/canned-replies',
  );
}

/** Every reply in the system — admin only (403 otherwise). */
export function fetchAllCannedReplies() {
  return apiGet<{ data: CannedReply[] }>('/canned-replies?scope=all');
}

export function createCannedReply(input: {
  projectId?: number | null;
  title: string;
  body: string;
}) {
  return apiPost<{ data: CannedReply }>('/canned-replies', input);
}

export function updateCannedReply(
  replyId: number,
  input: { title?: string; body?: string },
) {
  return apiPatch<{ data: CannedReply }>(`/canned-replies/${replyId}`, input);
}

export function deleteCannedReply(replyId: number) {
  return apiDelete<{ data: { success: boolean } }>(`/canned-replies/${replyId}`);
}

/**
 * Substitute `{requester_name}` and `{ticket_code}` when a snippet is inserted
 * into the composer.
 *
 * Mirrors `cannedReplyService.renderCannedReply` on the backend exactly,
 * including the two rules that matter in front of a client: an UNKNOWN
 * placeholder is left as written (it is far more likely to be literal prose
 * than a typo'd variable), and a KNOWN one with no value resolves to the empty
 * string rather than pasting `{requester_name}` into a reply. Substitution is
 * single-pass, so a value containing a placeholder is not re-scanned.
 *
 * Done here rather than server-side because insertion is a local edit in a
 * textarea the user then keeps typing in — a round-trip per insert would be
 * latency for nothing.
 */
export function renderCannedReply(
  body: string,
  context: { requesterName?: string | null; ticketCode?: string | null },
): string {
  const values: Record<string, string> = {
    requester_name: context.requesterName ?? '',
    ticket_code: context.ticketCode ?? '',
  };
  return body.replace(/\{([a-z_]+)\}/g, (match, name: string) => {
    const value = values[name];
    return value === undefined ? match : value;
  });
}

// ---------------------------------------------------------------------------
// Triage: the one new mutation (owner / tier / move-to-open already exist)
// ---------------------------------------------------------------------------

export function setTaskCategory(taskId: number, categoryId: number | null) {
  return apiPatch<{ data: { id: number; categoryId: number | null; categoryName: string | null } }>(
    `/tasks/${taskId}/category`,
    { categoryId },
  );
}
